import React, { useEffect, useState } from "react";
import { unitDisplay } from "../lib/constants";
import { toast } from "sonner";
import { ShoppingCart, CheckSquare, Square, Receipt, Tag } from "lucide-react";
import { api, arabicError } from "../lib/api";
import SaleEntryModal from "../components/SaleEntryModal";
import PhysicalInvoices from "../components/PhysicalInvoices";
import EditPhotoModal from "../components/EditPhotoModal";
import { useFeatures } from "../context/FeatureContext";

const ProductCard = ({ p, selected, multi, onToggle, onSell, isEmployee, onEditCode }) => (
  <div className={`bg-white border-2 rounded-xl overflow-hidden flex flex-col ${selected ? "border-[#09637E] ring-2 ring-[#09637E]/30" : "border-[#7AB2B2]"}`} data-testid={`sale-product-${p.id}`}>
    <div className="relative aspect-square bg-[#EBF4F6]">
      <img src={p.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
      {multi && (
        <button type="button" onClick={() => onToggle(p)} data-testid={`sale-select-${p.id}`}
          className="absolute top-2 right-2 w-10 h-10 rounded-lg bg-white/95 flex items-center justify-center text-[#09637E] shadow">
          {selected ? <CheckSquare /> : <Square />}
        </button>
      )}
      {p.product_code && <span className="absolute bottom-2 left-2 bg-[#09637E] text-white text-[11px] font-bold px-2 py-1 rounded" dir="ltr" data-testid={`sale-code-${p.id}`}>{p.product_code}</span>}
      {isEmployee && (
        <button type="button" onClick={() => onEditCode(p)} data-testid={`sale-edit-code-${p.id}`} title="تعديل كود المنتج"
          className="absolute bottom-2 right-2 h-7 px-2 rounded bg-white/95 text-[#09637E] text-[11px] font-bold flex items-center gap-1 shadow"><Tag size={12} /> تعديل الكود</button>
      )}
    </div>
    <div className="p-3 space-y-1 flex-1">
      <div className="font-bold text-[#333333] text-sm truncate">{p.display_name || `${p.category} · ${p.type}`}</div>
      <div className="text-xs text-[#666666]">{p.size} · {p.color} · {p.country}</div>
      <div className="text-[#09637E] font-extrabold" data-testid={`sale-price-${p.id}`}>${Number(p.price_usd || 0).toFixed(2)} <span className="text-xs font-normal text-[#666666]">/ {unitDisplay(p.unit).short}</span></div>
      <div className={`text-xs font-bold ${p.quantity_available > 0 ? "text-green-700" : "text-red-600"}`} data-testid={`sale-qty-${p.id}`}>
        {p.quantity_available > 0 ? `المتوفر: ${p.quantity_available} ${unitDisplay(p.unit).short}` : "غير متوفر"}
      </div>
      {p.reserved_quantity > 0 && <div className="text-xs font-bold text-amber-700" data-testid={`sale-reserved-${p.id}`}>محجوز: {p.reserved_quantity} م (طلبات المتجر العام)</div>}
    </div>
    <button type="button" onClick={() => onSell(p)} disabled={!(p.quantity_available > 0)} data-testid={`sale-sell-${p.id}`}
      className="m-3 mt-0 h-11 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50">
      <ShoppingCart size={16} /> بيع
    </button>
  </div>
);

export default function PhysicalSales() {
  const [data, setData] = useState({ actor: null, items: [] });
  const [sales, setSales] = useState([]);
  const [multi, setMulti] = useState(false);
  const [selected, setSelected] = useState([]);
  const [saleFor, setSaleFor] = useState(null);
  const [editCode, setEditCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("products");
  const [codeQuery, setCodeQuery] = useState("");
  const q = codeQuery.trim().toLowerCase();
  const shown = q ? data.items.filter((p) => (p.product_code || "").toLowerCase().includes(q)) : data.items;
  const { permissions } = useFeatures();

  const load = () => Promise.all([api.get("/sales/products"), api.get("/sales")])
    .then(([p, s]) => { setData(p.data); setSales(s.data); }).catch((e) => toast.error(arabicError(e))).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const toggle = (p) => setSelected((s) => s.find((x) => x.id === p.id) ? s.filter((x) => x.id !== p.id) : [...s, p]);
  const isEmployee = data.actor?.actor_type === "employee";

  return (
    <div className="space-y-6" data-testid="physical-sales-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-[#333333] flex items-center gap-2"><ShoppingCart className="text-[#09637E]" /> البيع الفيزيائي</h1>
          <p className="text-[#666666] mt-1 text-sm">{isEmployee ? `تسجيل مبيعات المحل باسمك: ${data.actor?.actor_name}` : "سجّل مبيعات المحل المباشرة من منتجاتك الحالية"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {(!isEmployee || (permissions || []).includes("physical_invoices")) && (
            <button type="button" onClick={() => setTab(tab === "invoices" ? "products" : "invoices")} data-testid="sales-tab-invoices"
              className={`h-11 px-4 rounded-lg font-bold flex items-center gap-2 ${tab === "invoices" ? "bg-[#09637E] text-white" : "border-2 border-[#09637E] text-[#09637E]"}`}>
              <Receipt size={16} /> {tab === "invoices" ? "المنتجات" : "فواتير"}
            </button>
          )}
          <button type="button" onClick={() => { setMulti((m) => !m); setSelected([]); }} data-testid="multi-select-toggle"
            className={`h-11 px-4 rounded-lg font-bold border-2 border-[#09637E] flex items-center gap-2 ${multi ? "bg-[#09637E] text-white" : "text-[#09637E] bg-white hover:bg-[#EBF4F6]"}`}>
            <CheckSquare size={18} /> {multi ? "إلغاء التحديد المتعدد" : "حدد أكثر من منتج"}
          </button>
          {multi && (
            <button type="button" disabled={selected.length === 0} onClick={() => setSaleFor(selected)} data-testid="multi-sell-btn"
              className="h-11 px-5 rounded-lg bg-[#09637E] hover:bg-[#088395] text-white font-bold flex items-center gap-2 disabled:opacity-50">
              <ShoppingCart size={18} /> بيع ({selected.length})
            </button>
          )}
        </div>
      </div>

      {loading ? <div className="text-center text-[#666666] p-8">جارٍ التحميل...</div> : data.items.length === 0 ? (
        <div className="bg-white border border-[#7AB2B2] rounded-xl p-10 text-center text-[#666666]" data-testid="sale-products-empty">لا توجد منتجات بعد</div>
      ) : (
        <>
          <div className="bg-white border border-[#7AB2B2] rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-2" data-testid="sale-code-search-box">
            <label htmlFor="sale-code-search" className="text-sm font-bold text-[#333333] whitespace-nowrap">البحث بكود المنتج</label>
            <input id="sale-code-search" type="text" dir="ltr" value={codeQuery} onChange={(e) => setCodeQuery(e.target.value)} placeholder="IMD-P-000094"
              className="h-10 flex-1 rounded-lg border border-[#7AB2B2] px-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#088395]" data-testid="sale-code-search" />
            {q && <button type="button" onClick={() => setCodeQuery("")} className="h-10 px-3 rounded-lg text-sm font-bold text-[#09637E] bg-[#EBF4F6]" data-testid="sale-code-search-clear">مسح</button>}
          </div>
          {shown.length === 0 ? (
            <div className="bg-white border border-[#7AB2B2] rounded-xl p-10 text-center text-[#666666]" data-testid="sale-code-search-empty">لم يتم العثور على المنتج</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="sale-products-grid">
              {shown.map((p) => (
                <ProductCard key={p.id} p={p} multi={multi} selected={!!selected.find((x) => x.id === p.id)} onToggle={toggle} onSell={(prod) => setSaleFor([prod])} isEmployee={isEmployee} onEditCode={setEditCode} />
              ))}
            </div>
          )}
        </>
      )}

      {tab === "invoices" && <PhysicalInvoices sales={sales} />}

      <section className="space-y-3" data-testid="recent-sales">
        <h2 className="text-xl font-extrabold text-[#333333] flex items-center gap-2"><Receipt className="text-[#09637E]" /> {isEmployee ? "مبيعاتي الأخيرة" : "آخر المبيعات"}</h2>
        <div className="bg-white border border-[#7AB2B2] rounded-xl overflow-hidden">
          {sales.length === 0 ? <div className="p-6 text-center text-[#666666]" data-testid="recent-sales-empty">لا توجد مبيعات مسجّلة بعد</div> : sales.slice(0, 20).map((s) => (
            <div key={s.id} className="p-4 border-b border-[#EBF4F6] last:border-0 flex flex-wrap items-center gap-3" data-testid={`sale-row-${s.id}`}>
              <div className="flex-1 min-w-[180px]">
                <div className="font-bold text-[#333333] text-sm">{s.items.map((i) => `${i.product_code || i.display_name} × ${i.quantity} ${unitDisplay(data.items.find((p) => p.id === i.product_id)?.unit).short}`).join(" ، ")}</div>
                <div className="text-xs text-[#666666] mt-1">{s.customer_name ? `الزبون: ${s.customer_name} · ` : ""}{s.channel === "individual_online" ? <span className="text-[#09637E] font-bold">بيع الأونلاين الفردي · </span> : null}باع: <span data-testid={`sale-actor-${s.id}`}>{s.actor_name}</span> ({s.actor_type === "employee" ? "موظف" : "التاجر"}) · {new Date(s.sale_datetime).toLocaleString("ar-SY")}</div>
              </div>
              <div className="text-right">
                <div className="font-extrabold text-[#09637E]">${Number(s.final_total).toFixed(2)}</div>
                {s.balance_due > 0 && <div className="text-xs text-amber-700 font-bold">متبقٍ: ${s.balance_due.toFixed(2)}</div>}
              </div>
            </div>
          ))}
        </div>
      </section>

      {editCode && <EditPhotoModal photo={editCode} codeOnly onClose={() => setEditCode(null)} onSaved={load} />}
      {saleFor && (
        <SaleEntryModal products={saleFor} onClose={() => setSaleFor(null)}
          onSaved={() => { setSaleFor(null); setSelected([]); setMulti(false); load(); }} />
      )}
    </div>
  );
}
